import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AUDITION_TYPES, AUDITION_STATUSES } from "@/lib/crm";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";

export default function AuditionFormDialog({
  open, onOpenChange, castingDirectors, onCreated, audition,
}) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const isEdit = !!audition;
  const [form, setForm] = useState({
    project: "", role: "", production_type: "",
    casting_director_id: "",
    casting_source: "",
    audition_type: "in_person",
    status: "new",
    notes: "",
    feedback: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (audition) {
      setForm({
        project: audition.project || "",
        role: audition.role || "",
        production_type: audition.production_type || "",
        casting_director_id: audition.casting_director_id || "",
        casting_source: audition.casting_source || "",
        audition_type: audition.audition_type || "in_person",
        status: audition.status || "new",
        notes: audition.notes || "",
        feedback: audition.feedback || "",
      });
    } else {
      setForm({
        project: "", role: "", production_type: "",
        casting_director_id: castingDirectors?.[0]?.contact_id || "",
        casting_source: "", audition_type: "in_person", status: "new",
        notes: "", feedback: "",
      });
    }
  }, [open, audition, castingDirectors]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.project.trim() || !form.casting_director_id) {
      toast.error(t("aud.form.required"));
      return;
    }
    setSaving(true);
    try {
      let r;
      if (isEdit) {
        r = await api.patch(`/crm/auditions/${audition.audition_id}`, form);
      } else {
        r = await api.post("/crm/auditions", form);
      }
      toast.success(t("aud.form.saved"));
      onOpenChange?.(false);
      onCreated?.(r.data);
    } catch (err) {
      toast.error(t("aud.form.fail"));
    } finally { setSaving(false); }
  };

  // (translations are handled by t() above)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="aud-form-dialog"
        className="max-w-2xl bg-stone-950 border-stone-800 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-stone-100">
            {isEdit ? t("aud.form.title_edit") : t("aud.form.title_new")}
          </DialogTitle>
          <DialogDescription className="text-stone-400 text-sm">
            {t("aud.form.cd_hint")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="label-cap">{t("aud.form.project")}</label>
            <input
              data-testid="aud-form-project"
              required
              value={form.project}
              onChange={(e) => set("project", e.target.value)}
              className="script-input w-full"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-cap">{t("aud.form.role")}</label>
              <input
                data-testid="aud-form-role"
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
                className="script-input w-full"
              />
            </div>
            <div>
              <label className="label-cap">{t("aud.form.production_type")}</label>
              <input
                data-testid="aud-form-production"
                value={form.production_type}
                onChange={(e) => set("production_type", e.target.value)}
                placeholder={t("aud.form.production_ph")}
                className="script-input w-full"
              />
            </div>
          </div>

          <div>
            <label className="label-cap">{t("aud.form.cd")}</label>
            <select
              data-testid="aud-form-cd"
              value={form.casting_director_id}
              onChange={(e) => set("casting_director_id", e.target.value)}
              className="script-input w-full"
              required
            >
              <option value="">{t("aud.form.cd_choose")}</option>
              {castingDirectors?.map((c) => (
                <option key={c.contact_id} value={c.contact_id}>
                  {c.first_name} {c.last_name}{c.company ? ` — ${c.company}` : ""}
                </option>
              ))}
            </select>
            {(!castingDirectors || castingDirectors.length === 0) && (
              <p className="text-xs text-amber-400 mt-1">{t("aud.form.cd_none")}</p>
            )}
          </div>

          <div>
            <label className="label-cap">{t("aud.form.source")}</label>
            <input
              data-testid="aud-form-source"
              value={form.casting_source}
              onChange={(e) => set("casting_source", e.target.value)}
              placeholder={t("aud.form.source_ph")}
              className="script-input w-full"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-cap">{t("aud.form.audition_type")}</label>
              <select
                data-testid="aud-form-type"
                value={form.audition_type}
                onChange={(e) => set("audition_type", e.target.value)}
                className="script-input w-full"
              >
                {AUDITION_TYPES.map((k) => (
                  <option key={k} value={k}>{t(`aud.type.${k}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-cap">{t("aud.form.status")}</label>
              <select
                data-testid="aud-form-status"
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
                className="script-input w-full"
              >
                {AUDITION_STATUSES.map((k) => (
                  <option key={k} value={k}>{t(`aud.status.${k}`)}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label-cap">{t("aud.form.notes")}</label>
            <textarea
              data-testid="aud-form-notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder={t("aud.form.notes_ph")}
              rows={3}
              className="script-input w-full resize-none"
            />
          </div>

          {isEdit && (
            <div>
              <label className="label-cap">{t("aud.form.feedback")}</label>
              <textarea
                data-testid="aud-form-feedback"
                value={form.feedback}
                onChange={(e) => set("feedback", e.target.value)}
                placeholder={t("aud.form.feedback_ph")}
                rows={3}
                className="script-input w-full resize-none"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange?.(false)} className="ghost-btn">
              {t("action.cancel")}
            </button>
            <button
              type="submit"
              data-testid="aud-form-save"
              disabled={saving || !form.casting_director_id}
              className="pill-btn"
            >
              {saving ? <><Loader2 size={14} className="animate-spin" /> {t("crm.form.saving")}</> : t("aud.form.save")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
